import { useState, useEffect } from "react";
import { api } from "../App";
import { handleApiError } from "../utils/errorHandler";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";
import { Loader2, TrendingUp, Building2, Wallet, FileText } from "lucide-react";
import { PageLoader } from "../components/ui/page-loader";
import { useTranslation } from "../contexts/I18nContext";

export default function ReportsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [byAccount, setByAccount] = useState([]);
  const [byCostCenter, setByCostCenter] = useState([]);
  const [monthly, setMonthly] = useState([]);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const [sumRes, accRes, ccRes, monthRes] = await Promise.all([
          api.get("/reports/summary"),
          api.get("/reports/by-account"),
          api.get("/reports/by-cost-center"),
          api.get("/reports/monthly"),
        ]);
        setSummary(sumRes.data);
        setByAccount(accRes.data);
        setByCostCenter(ccRes.data);
        setMonthly(monthRes.data);
      } catch (err) {
        handleApiError(err, t("errors.loadReport"));
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [t]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val);

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("reports.title")}</h1>
        <p className="text-muted-foreground text-sm">Analisi dettagliata della tua contabilità</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList data-testid="report-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <TrendingUp className="h-4 w-4 mr-2" />{t('common.overview')}</TabsTrigger>
          <TabsTrigger value="by-type" data-testid="tab-by-type">
            <FileText className="h-4 w-4 mr-2" />
            Per Tipo
          </TabsTrigger>
          <TabsTrigger value="by-account" data-testid="tab-by-account">
            <Wallet className="h-4 w-4 mr-2" />
            Per Conto
          </TabsTrigger>
          <TabsTrigger value="by-cost-center" data-testid="tab-by-cost-center">
            <Building2 className="h-4 w-4 mr-2" />
            Per Centro Costo
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('common.monthlyTrend')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {monthly.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthly}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="mese" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="totale" stroke="hsl(var(--primary))" strokeWidth={2} name="Totale" />
                      <Line type="monotone" dataKey="pagato" stroke="#10b981" strokeWidth={2} name="Pagato" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">{t('common.noData')}</div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Volume Mensile</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {monthly.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthly}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="mese" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => value} />
                        <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Fatture" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                      Nessun dato
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('common.summary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-muted rounded-md">
                    <span>Totale fatture</span>
                    <span className="font-mono font-bold">{summary?.totale_fatture || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-500/10 rounded-md">
                    <span>Importo totale</span>
                    <span className="font-mono font-bold">{formatCurrency(summary?.totale_importo || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-500/10 rounded-md">
                    <span>Totale pagato</span>
                    <span className="font-mono font-bold text-green-600">{formatCurrency(summary?.totale_pagato || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-500/10 rounded-md">
                    <span>Da pagare</span>
                    <span className="font-mono font-bold text-red-600">{formatCurrency(summary?.totale_da_pagare || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="by-type">
          <Card>
            <CardHeader>
              <CardTitle>Dettaglio per Tipo Documento</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('invoices.documentType')}</TableHead>
                    <TableHead className="text-right">Quantità</TableHead>
                    <TableHead className="text-right">{t("common.total")}</TableHead>
                    <TableHead className="text-right">{t('invoices.paid')}</TableHead>
                    <TableHead className="text-right">{t('common.toPay')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary?.per_tipo && Object.entries(summary.per_tipo).length > 0 ? (
                    Object.entries(summary.per_tipo).map(([tipo, data]) => (
                      <TableRow key={tipo}>
                        <TableCell className="font-medium">{tipo}</TableCell>
                        <TableCell className="text-right font-mono">{data.count}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(data.totale)}</TableCell>
                        <TableCell className="text-right font-mono text-green-600">{formatCurrency(data.pagato)}</TableCell>
                        <TableCell className="text-right font-mono text-red-600">
                          {formatCurrency(data.totale - data.pagato)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{t('common.noData')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-account">
          <Card>
            <CardHeader>
              <CardTitle>Dettaglio per Conto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] mb-6">
                {byAccount.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byAccount} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="nome" type="category" tick={{ fontSize: 12 }} width={150} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="totale" fill="hsl(var(--primary))" name="Totale" />
                      <Bar dataKey="pagato" fill="#10b981" name="Pagato" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">{t('common.noData')}</div>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('movements.account')}</TableHead>
                    <TableHead className="text-right">{t('invoices.title')}</TableHead>
                    <TableHead className="text-right">{t("common.total")}</TableHead>
                    <TableHead className="text-right">{t('invoices.paid')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byAccount.length > 0 ? (
                    byAccount.map((acc) => (
                      <TableRow key={acc.nome}>
                        <TableCell className="font-medium">{acc.nome}</TableCell>
                        <TableCell className="text-right font-mono">{acc.count}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(acc.totale)}</TableCell>
                        <TableCell className="text-right font-mono text-green-600">{formatCurrency(acc.pagato)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">{t('common.noData')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-cost-center">
          <Card>
            <CardHeader>
              <CardTitle>Dettaglio per Centro di Costo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] mb-6">
                {byCostCenter.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byCostCenter} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="nome" type="category" tick={{ fontSize: 12 }} width={150} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="totale" fill="hsl(var(--primary))" name="Totale" />
                      <Bar dataKey="pagato" fill="#10b981" name="Pagato" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">{t('common.noData')}</div>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('costCenters.title')}</TableHead>
                    <TableHead className="text-right">{t('invoices.title')}</TableHead>
                    <TableHead className="text-right">{t("common.total")}</TableHead>
                    <TableHead className="text-right">{t('invoices.paid')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byCostCenter.length > 0 ? (
                    byCostCenter.map((cc) => (
                      <TableRow key={cc.nome}>
                        <TableCell className="font-medium">{cc.nome}</TableCell>
                        <TableCell className="text-right font-mono">{cc.count}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(cc.totale)}</TableCell>
                        <TableCell className="text-right font-mono text-green-600">{formatCurrency(cc.pagato)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">{t('common.noData')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
